import { CSSProperties } from 'react'
import { NavbarButtonSet } from '@/constants/app-options'

export interface NavbarButtonBaseProps {
  disabled?: boolean
  premiumSource?: string
  onClick?: () => void
}

export type NavbarButtonId =
  | 'logoIconAndText'
  | 'logoIconWithPopover'
  | 'history'
  | 'backButton'
  | 'zoom'
  | 'tryPremium'
  | 'tryPremiumOrange'
  | 'trySuper'
  | 'signIn'
  | 'exportImage'
  | 'downloadAll'
  | 'userMenu'

/** Per-button callback overrides. When a callback is provided for a button,
 *  it replaces the button's default behaviour. */
export interface NavbarButtonCallbacks {
  /** When set, `downloadAll` downloads these images instead of the canvas scenes. */
  downloadAll?: () => string[] | Promise<string[]>
  onSignOut?: () => void
}

export interface NavbarModeConfig {
  left: NavbarButtonId[]
  right: NavbarButtonId[]
  style: CSSProperties
  callbacks?: NavbarButtonCallbacks
}

export type NavbarMode = 'create' | 'editor' | 'customButtonSet'

const NAVBAR_WITH_BORDER_STYLE: CSSProperties = {
  borderBottom: '1px solid #e5e5e5',
  boxShadow: 'none',
}

const NAVBAR_WITH_SHADOW_STYLE: CSSProperties = {
  borderBottom: 'none',
  boxShadow: '0px 0px 6px 0px rgba(0, 0, 0, 0.08)',
}

const getNavbarCustomButtonSets = (
  navbarButtonSet: NavbarButtonSet,
  callbacks?: NavbarButtonCallbacks
): Omit<NavbarModeConfig, 'style'> => {
  if (navbarButtonSet === 'aiFashion') {
    return {
      left: ['backButton'],
      right: ['zoom', 'tryPremium', 'trySuper', 'downloadAll', 'userMenu'],
      callbacks,
    }
  }
  if (navbarButtonSet === 'photoshoot') {
    return {
      left: ['backButton'],
      right: ['tryPremium', 'trySuper', 'downloadAll', 'userMenu'],
      callbacks,
    }
  }
  return {
    left: [],
    right: [],
  }
}

/**
 * Returns the Metsikon paywall source for the "Try Premium" button based on
 * the current navbar mode and button set. Returns `undefined` when no
 * product-specific source applies (i.e. standard editor / create modes).
 */
export function getNavbarPremiumSource(
  navbarMode: NavbarMode,
  navbarButtonSet?: NavbarButtonSet,
): string | undefined {
  if (navbarMode !== 'customButtonSet') return undefined
  if (navbarButtonSet === 'aiFashion') return 'BtSubscriptionAIModels'
  if (navbarButtonSet === 'photoshoot') return 'BtSubscriptionPhotoshoot'
  return undefined
}

export const getNavbarConfig = (
  navbarMode: NavbarMode,
  navbarButtonSet: NavbarButtonSet,
  callbacks?: NavbarButtonCallbacks
): NavbarModeConfig => ({
  create: {
    left: ['logoIconAndText'],
    right: ['tryPremium', 'trySuper', 'signIn', 'userMenu'],
    style: NAVBAR_WITH_BORDER_STYLE,
  },
  editor: {
    left: ['logoIconWithPopover', 'history'],
    right: ['zoom', 'tryPremium', 'trySuper', 'signIn', 'exportImage', 'userMenu'],
    style: NAVBAR_WITH_SHADOW_STYLE,
  },
  customButtonSet: {
    ...getNavbarCustomButtonSets(navbarButtonSet, callbacks),
    style: NAVBAR_WITH_SHADOW_STYLE,
  },
} as Record<NavbarMode, NavbarModeConfig>)[navbarMode]
